import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';
import { IS_LOAD, IS_SEARCH } from 'src/app/shared/models/load.models';
import { QcMerchantResponse } from 'src/app/shared/models/qcResponse.model';
import { EnvironmentFacadeService } from 'src/app/shared/services/environment.facade.service';
import { LiteralsFacadeService } from 'src/app/shared/services/literals.facade.service';
import { PaginatorFacadeService } from 'src/app/shared/services/paginator.facade.service';
import {
  Category,
  Club,
  Country,
  Couponset,
  LMembersResponse,
  LoyaltyMember,
  MemberStatus,
  PointSource,
  Schema,
  Store,
} from '../models/members-search.models';
import { MembersSearchFacadeService } from './members-search.facade.service';

@Injectable({ providedIn: 'root' })
export class LoyaltyMembersService {
  constructor(
    private http: HttpClient,
    private environmentFacadeService: EnvironmentFacadeService,
    private msFacade: MembersSearchFacadeService,
    private paginatorFacade: PaginatorFacadeService,
    private literalsFacade: LiteralsFacadeService,
  ) {}

  countries(): Observable<Country[]> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/countries';

    return this.http.get<Country[]>(url, {
      withCredentials: true,
      context: new HttpContext().set(IS_LOAD, true),
    });
  }

  schemas(): Observable<Schema[]> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/loyalty/schemas';

    return this.http.get<Schema[]>(url, {
      withCredentials: true,
      context: new HttpContext().set(IS_LOAD, true),
    });
  }

  stores(): Observable<Store[]> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/system/stores';

    return this.http.get<Store[]>(url, {
      withCredentials: true,
      context: new HttpContext().set(IS_LOAD, true),
    });
  }

  categories(): Observable<Category[]> {
    const url =
      this.environmentFacadeService.getEnvironment() +
      '/rest/v1/loyalty/lookup-properties/member/categories';

    return this.http.get<Category[]>(url, {
      withCredentials: true,
      context: new HttpContext().set(IS_LOAD, true),
    });
  }

  pointSources(): Observable<PointSource[]> {
    const url =
      this.environmentFacadeService.getEnvironment() +
      '/rest/v1/loyalty/lookup-properties/pointsourches';

    return this.http.get<PointSource[]>(url, {
      withCredentials: true,
      context: new HttpContext().set(IS_LOAD, true),
    });
  }

  loyaltyMembers(): Observable<LoyaltyMember[]> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/tags/LOYALTY_MEMBER';

    return this.http.get<LoyaltyMember[]>(url, {
      withCredentials: true,
      context: new HttpContext().set(IS_LOAD, true),
    });
  }

  customer(): Observable<any> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/tags/CUSTOMER';

    return this.http.get<any>(url, {
      withCredentials: true,
      context: new HttpContext().set(IS_LOAD, true),
    });
  }

  couponsets(): Observable<Couponset[]> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/couponset';

    return this.http.get<Couponset[]>(url, {
      withCredentials: true,
      context: new HttpContext().set(IS_LOAD, true),
    });
  }

  clubs(): Observable<Club[]> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/member/clubs/all';

    return this.http.get<Club[]>(url, {
      withCredentials: true,
      context: new HttpContext().set(IS_LOAD, true),
    });
  }

  statuses(): Observable<MemberStatus[]> {
    const url =
      this.environmentFacadeService.getEnvironment() +
      '/rest/v1/loyalty/lookup-properties/member/statuses';

    return this.http.get<MemberStatus[]>(url, {
      withCredentials: true,
      context: new HttpContext().set(IS_LOAD, true),
    });
  }

  attributeCodes(): Observable<QcMerchantResponse> {
    const url =
      this.environmentFacadeService.getEnvironment() + '/rest/v1/loyalty/members/attribute-codes';

    return this.http.get<QcMerchantResponse>(url, {
      withCredentials: true,
      context: new HttpContext().set(IS_LOAD, true),
    });
  }

  search(): Observable<LMembersResponse> {
    const url =
      this.environmentFacadeService.getEnvironment() +
      `/rest/v1/loyalty/members/findByFilter?start=${this.paginatorFacade.pageIndex * this.paginatorFacade.itemsNumber}&pageSize=${this.paginatorFacade.itemsNumber}&sortField=${this.paginatorFacade.selectedSortField === this.literalsFacade.literals.literals['liquid.search.sort.lastname'] ? 'LASTNAME' : this.paginatorFacade.selectedSortField === this.literalsFacade.literals.literals['liquid.search.sort.firstname'] ? 'FIRSTNAME' : 'REGISTRATION_DATE'}&sortOrder=${
        this.paginatorFacade.selectedOrder ===
        this.literalsFacade.literals.literals['liquid.search.sort.asc']
          ? 'ASC'
          : 'DESC'
      }`;

    const body = this.getLMBody();

    return this.http.post<LMembersResponse>(url, body, {
      withCredentials: true,
      context: new HttpContext().set(IS_SEARCH, true),
    });
  }

  count(): Observable<number> {
    const url =
      this.environmentFacadeService.getEnvironment() + '/rest/v1/loyalty/members/countByFilter';
    const body = this.getLMBody();

    return this.http.post<number>(url, body, {
      withCredentials: true,
      context: new HttpContext().set(IS_SEARCH, true),
    });
  }

  getLMBody() {
    let body: any = {
      lastName: this.msFacade.lastName ? this.msFacade.lastName.trim() : '',
      firstName: this.msFacade.firstName,
      cardNumber: this.msFacade.cardNumber,
      includeInactive: this.msFacade.includeInactive,
      emailAddress: this.msFacade.email,
      mobile: this.msFacade.mobile,
      memberCountryId: this.msFacade.country ? this.msFacade.country : null,
      registrationStoreId: this.msFacade.registrationStore ? this.msFacade.registrationStore : null,
      loyaltySchemaId: this.msFacade.schema ? this.msFacade.schema : null,
      registrationDateFrom: this.msFacade.registrationDateFrom
        ? this.msFacade.registrationDateFrom
        : null,
      registrationDateUntil: this.msFacade.registrationDateUntil
        ? this.msFacade.registrationDateUntil
        : null,
      tagId: this.msFacade.memberTag ? this.msFacade.memberTag : null,
      tableMemberCategoryId: this.msFacade.memberCategory ? this.msFacade.memberCategory : null,
      clubId: this.msFacade.club ? this.msFacade.club : null,
    };

    if (this.msFacade.attributeType) {
      const attributeValue =
        this.msFacade.attributeType === 'STRING' || this.msFacade.attributeType === 'INTEGER'
          ? this.msFacade.attributeValue
            ? this.msFacade.attributeValue
            : undefined
          : this.msFacade.attributeType === 'BOOLEAN'
            ? this.msFacade.attributeBoolValue
            : {
                dateFrom: this.msFacade.attributeDateFrom ? this.msFacade.attributeDateFrom : null,
                dateUntil: this.msFacade.attributeDateUntil
                  ? this.msFacade.attributeDateUntil
                  : null,
              };

      body.attribute = {
        attributeType: this.msFacade.attributeType,
        attributeName: this.msFacade.attributeName ? this.msFacade.attributeName : undefined,
        attributeValue: attributeValue ? attributeValue : undefined,
      };
    }

    if (this.msFacade.qivosID) {
      body.systemCode = this.msFacade.qivosID;
    }

    return body;
  }
}
